import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { FileUp, Loader2, Send, X } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/lib/api";

const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-ink outline-none transition-all duration-300 placeholder:text-gray-400 focus:border-navy focus:ring-4 focus:ring-navy/10";

const ApplicationModal = ({ role, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please attach your resume (PDF, DOC or DOCX, up to 5 MB).");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("role", role);
      fd.append("message", form.message);
      fd.append("resume", file);
      await axios.post(`${API}/applications`, fd);
      toast.success("Application submitted. Our team will review it and get back to you.");
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit your application. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] flex items-center justify-center bg-navy-900/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        data-testid="application-modal"
        initial={{ opacity: 0, y: 32, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 32, scale: 0.97 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-8 shadow-2xl sm:p-10"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-gold-dark">
              Job Application
            </p>
            <h2 className="mt-2 font-heading text-xl font-bold leading-snug text-ink">{role}</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close application form"
            data-testid="application-close"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 text-fog transition-colors duration-300 hover:text-ink"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="mt-7 space-y-5" data-testid="application-form">
          <input
            required
            placeholder="Full name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            data-testid="application-name-input"
            className={inputCls}
          />
          <input
            required
            type="email"
            placeholder="Email address"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            data-testid="application-email-input"
            className={inputCls}
          />
          <input
            required
            type="tel"
            placeholder="Phone number"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            data-testid="application-phone-input"
            className={inputCls}
          />
          <textarea
            rows={4}
            placeholder="Tell us briefly about yourself (optional)"
            value={form.message}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            data-testid="application-message-input"
            className={`${inputCls} resize-none`}
          />
          <div>
            <label
              htmlFor="resume-upload"
              data-testid="resume-upload-label"
              className="flex cursor-pointer items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-navy/20 bg-mist px-5 py-8 text-center transition-all duration-300 hover:border-gold hover:bg-gold/5"
            >
              <FileUp className="h-5 w-5 shrink-0 text-royal" />
              <span className="text-sm text-fog">
                {file ? (
                  <span className="font-semibold text-navy">{file.name}</span>
                ) : (
                  <>
                    <span className="font-semibold text-navy">Upload your resume</span>
                    <br />
                    PDF, DOC or DOCX · up to 5 MB
                  </>
                )}
              </span>
            </label>
            <input
              id="resume-upload"
              type="file"
              accept=".pdf,.doc,.docx"
              className="hidden"
              data-testid="resume-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="application-submit-button"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-navy py-4 font-heading text-sm font-bold uppercase tracking-widest text-white transition-all duration-300 hover:bg-navy-800 hover:shadow-lg hover:shadow-navy/25 disabled:opacity-60"
          >
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
              </>
            ) : (
              <>
                Submit Application <Send className="h-4 w-4" />
              </>
            )}
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default ApplicationModal;
